import { isExpiredSignedMediaUrl } from '@/lib/expiring-media-url';

/** Runtime-only delivery projections. Signed URLs must never enter persisted canvas state. */
export interface MediaDeliveryVariant {
  url: string;
  width?: number;
  height?: number;
  bytes?: number;
  duration?: number;
  mime_type?: string;
  content_type?: string;
}
export interface MediaDelivery {
  asset_id: string;
  status: 'ready' | 'processing' | 'unavailable';
  original?: MediaDeliveryVariant;
  image?: { thumbnail?: MediaDeliveryVariant; preview?: MediaDeliveryVariant };
  video?: { preview?: MediaDeliveryVariant; playback?: MediaDeliveryVariant; poster?: MediaDeliveryVariant };
  expires_at?: number;
}
export interface DeliverySnapshot { loading: boolean; data?: MediaDelivery }
const EMPTY: DeliverySnapshot = Object.freeze({ loading: false });
const PENDING: DeliverySnapshot = Object.freeze({ loading: true });
const ERROR_DELAYS = [1_000, 3_000, 10_000];
const RETRY_DELAYS = [5_000, 10_000, 20_000, 30_000, 60_000, 60_000];
type Entry = { snapshot: DeliverySnapshot; listeners: Set<() => void>; attempts: number; failures: number; timer?: ReturnType<typeof setTimeout>; controller?: AbortController };

/** Batches only subscribed assets, deduplicates concurrent consumers and stops work on unmount. */
export class MediaDeliveryCache {
  private entries = new Map<string, Entry>();
  private pending = new Set<string>();
  private flushTimer?: ReturnType<typeof setTimeout>;
  constructor(private request: (ids: string[], signal: AbortSignal) => Promise<MediaDelivery[]>) {}
  get(id?: string | null): DeliverySnapshot { return id ? this.entries.get(id)?.snapshot ?? PENDING : EMPTY; }
  subscribe(id: string, listener: () => void): () => void {
    let entry = this.entries.get(id);
    if (!entry) {
      entry = { snapshot: EMPTY, listeners: new Set(), attempts: 0, failures: 0 };
      this.entries.set(id, entry);
    }
    const reopenedProcessing = !entry.listeners.size && entry.snapshot.data?.status === 'processing';
    if (reopenedProcessing) entry.attempts = 0;
    entry.listeners.add(listener);
    const expiry = entry.snapshot.data?.expires_at;
    if (reopenedProcessing || !entry.snapshot.data || (expiry && expiry * 1000 <= Date.now() + 30_000)) this.enqueue(id, entry);
    else this.schedule(id, entry);
    return () => {
      entry!.listeners.delete(listener);
      if (entry!.listeners.size) return;
      clearTimeout(entry!.timer);
      entry!.timer = undefined;
      this.pending.delete(id);
      const controller = entry!.controller;
      if (controller && ![...this.entries.values()].some(e => e.controller === controller && e.listeners.size)) controller.abort();
      // Keep a bounded memory cache for returning to a recently opened canvas.
      if (this.entries.size > 500) for (const [key, value] of this.entries) {
        if (!value.listeners.size && !value.controller) this.entries.delete(key);
        if (this.entries.size <= 500) break;
      }
    };
  }
  clear(): void {
    clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
    for (const entry of this.entries.values()) { clearTimeout(entry.timer); entry.controller?.abort(); }
    this.pending.clear();
    this.entries.clear();
  }
  refresh(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.attempts = 0;
    entry.failures = 0;
    this.enqueue(id, entry);
  }
  private emit(entry: Entry): void { entry.listeners.forEach(listener => listener()); }
  private enqueue(id: string, entry: Entry): void {
    if (entry.controller || this.pending.has(id) || !entry.listeners.size) return;
    clearTimeout(entry.timer);
    const expiry = entry.snapshot.data?.expires_at;
    entry.snapshot = expiry && expiry * 1000 <= Date.now()
      ? PENDING : { ...entry.snapshot, loading: true };
    this.emit(entry);
    this.pending.add(id);
    this.flushTimer ??= setTimeout(() => { this.flushTimer = undefined; void this.flush(); }, 0);
  }
  private schedule(id: string, entry: Entry): void {
    clearTimeout(entry.timer);
    if (!entry.listeners.size || entry.controller) return;
    const data = entry.snapshot.data;
    const delay = entry.failures ? ERROR_DELAYS[entry.failures - 1] : data?.status === 'processing'
      ? RETRY_DELAYS[entry.attempts - 1]
      : data?.expires_at ? Math.max(1_000, data.expires_at * 1000 - Date.now() - 30_000) : undefined;
    if (delay !== undefined) entry.timer = setTimeout(() => this.enqueue(id, entry), delay);
  }
  private async flush(): Promise<void> {
    const ids = [...this.pending].slice(0, 40);
    ids.forEach(id => this.pending.delete(id));
    if (this.pending.size) this.flushTimer = setTimeout(() => { this.flushTimer = undefined; void this.flush(); }, 0);
    if (!ids.length) return;
    const controller = new AbortController();
    const entries = ids.map(id => this.entries.get(id)!);
    entries.forEach(entry => { entry.controller = controller; entry.attempts += 1; });
    try {
      const results = await this.request(ids, controller.signal);
      if (controller.signal.aborted) return;
      const byId = new Map(results.map(item => [item.asset_id, item]));
      ids.forEach((id, index) => {
        const entry = entries[index];
        entry.failures = 0;
        entry.snapshot = { loading: false, data: byId.get(id) ?? { asset_id: id, status: 'unavailable' } };
      });
    } catch {
      // Existing durable media remains usable when delivery resolution fails.
      entries.forEach(entry => { entry.failures += 1; entry.snapshot = { ...entry.snapshot, loading: false }; });
    } finally {
      ids.forEach((id, index) => {
        const entry = entries[index];
        entry.controller = undefined;
        if (this.entries.get(id) !== entry) return;
        if (controller.signal.aborted) entry.snapshot = { ...entry.snapshot, loading: false };
        this.emit(entry);
        if (controller.signal.aborted && entry.listeners.size) this.enqueue(id, entry);
        else this.schedule(id, entry);
      });
    }
  }
}

// Reuse the image the user is already seeing when opening Edit or the lightbox.
const displayedImages = new Map<string, string>();
const decodedImageUrls = new Set<string>();
export function isMediaImageDecoded(url: string): boolean {
  return !isExpiredSignedMediaUrl(url) && decodedImageUrls.has(url);
}
export function rememberDecodedMediaImage(url: string): void {
  if (!url || isExpiredSignedMediaUrl(url)) return;
  decodedImageUrls.delete(url);
  decodedImageUrls.add(url);
  if (decodedImageUrls.size > 1000) decodedImageUrls.delete(decodedImageUrls.values().next().value!);
}
export function rememberDisplayedMedia(assetId: string | undefined, url: string): void {
  if (!assetId || !url || isExpiredSignedMediaUrl(url)) return;
  rememberDecodedMediaImage(url);
  displayedImages.delete(assetId);
  displayedImages.set(assetId, url);
  if (displayedImages.size > 200) displayedImages.delete(displayedImages.keys().next().value!);
}
export function getDisplayedMedia(assetId?: string | null): string | undefined {
  if (!assetId) return undefined;
  const url = displayedImages.get(assetId);
  if (url && isExpiredSignedMediaUrl(url)) {
    displayedImages.delete(assetId);
    decodedImageUrls.delete(url);
    return undefined;
  }
  return url;
}
export function clearDisplayedMedia(): void { displayedImages.clear(); decodedImageUrls.clear(); }
